
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Users, UserCheck, BookOpen, Plus } from 'lucide-react';
import { TeamCoachManagement } from '@/components/admin/TeamCoachManagement';
import { TeamRosterManagement } from '@/components/admin/TeamRosterManagement';
import { ProgramManagement } from '@/components/admin/ProgramManagement';
import { Program } from '@/types/program';

interface Team {
  team_coaches?: {
    id: string;
    role: string;
    is_active: boolean;
    profiles: {
      id: string;
      first_name: string;
      last_name: string;
    };
  }[];
  clubs?: {
    id: string;
  } | null;
}

interface TeamManagementTabsProps {
  team: Team;
  teamId: string;
  playerCount: number;
}

export const TeamManagementTabs = ({ team, teamId, playerCount }: TeamManagementTabsProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState<Program | null>(null);

  return (
    <Tabs defaultValue="roster" className="space-y-6">
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="roster" className="flex items-center gap-2">
          <Users className="h-4 w-4" />
          Roster ({playerCount})
        </TabsTrigger>
        <TabsTrigger value="coaches" className="flex items-center gap-2">
          <UserCheck className="h-4 w-4" />
          Coaches ({team?.team_coaches?.length || 0})
        </TabsTrigger>
        <TabsTrigger value="programs" className="flex items-center gap-2">
          <BookOpen className="h-4 w-4" />
          Programs
        </TabsTrigger>
      </TabsList>

      <TabsContent value="coaches" className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5" />
              Team Coaches
            </CardTitle>
            <CardDescription>
              Manage coaches assigned to this team
            </CardDescription>
          </CardHeader>
          <CardContent>
            {team?.clubs?.id ? (
              <TeamCoachManagement teamId={teamId} clubId={team.clubs.id} />
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No club assigned to this team</p>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="roster" className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Team Roster
            </CardTitle>
            <CardDescription>
              Manage players assigned to this team
            </CardDescription>
          </CardHeader>
          <CardContent>
            {team?.clubs?.id ? (
              <TeamRosterManagement teamId={teamId} clubId={team.clubs.id} />
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No club assigned to this team</p>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="programs" className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Training Programs
                </CardTitle>
                <CardDescription>
                  Manage training programs for this team
                </CardDescription>
              </div>
              <Button 
                onClick={() => setIsDialogOpen(true)}
                disabled={!team?.clubs?.id}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Program
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ProgramManagement 
              clubId={team?.clubs?.id} 
              teamId={teamId}
              isDialogOpen={isDialogOpen}
              setIsDialogOpen={setIsDialogOpen}
              editingProgram={editingProgram}
              setEditingProgram={setEditingProgram}
            />
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
};
